/**
 * Represents the configuration settings for a tab.
 *
 * This interface is used to define properties necessary for identifying
 * and managing a specific tab in a user interface or similar context.
 *
 * Properties:
 * - `elementId`: A string representing the unique identifier of the tab element.
 * - `tabIndex`: A number representing the index position of the tab.
 */
interface TabConfig {
    elementId: string;
    tabIndex: number;
}

/**
 * TabbedContent is a utility class designed to handle tab navigation and scrolling behavior
 * based on URL hash changes. It provides functionality to parse tab configuration from the
 * URL, find the corresponding tab and content element, and activate the relevant tab.
 */
export class TabbedContent {
    /**
     * SCROLL_BEHAVIOR defines the configuration for scrolling elements into view.
     *
     * This constant adheres to the ScrollIntoViewOptions interface, specifying
     * how the scroll operation should behave when triggered.
     *
     * Properties:
     * - behavior: Determines the scroll animation. It is set to 'smooth',
     *   causing the scrolling action to occur smoothly rather than an
     *   immediate jump.
     * - block: Specifies the vertical alignment inside the scrolling container.
     *   It is set to 'start', aligning the target element to the top of the container.
     */
    private static readonly SCROLL_BEHAVIOR: ScrollIntoViewOptions = {
        behavior: 'smooth',
        block: 'start',
    };

    /**
     * A CSS selector string used to identify and interact with a specific tab button element
     * within a list of extended tab content items. The selector contains a placeholder
     * ({index}) that should be replaced dynamically with the index of the desired element.
     *
     * Purpose:
     * - To locate the button within an indexed item of the extended tab content list,
     *   allowing developers to perform actions such as clicking or retrieving element properties.
     *
     * Structure:
     * - '.tab-content__extended-list__item': Represents an item in the extended list of tab content.
     * - '[data-item="{index}"]': Dynamically selects the item based on the 'index' provided.
     * - 'button': Targets the button within the selected item.
     *
     * Note:
     * Replace `{index}` with the actual index value before using the selector.
     */
    private static readonly TAB_BUTTON_SELECTOR = '.tab-content__extended-list__item[data-item="{index}"] button';

    /**
     * Represents the delay time in milliseconds applied when a tab is clicked.
     * This delay is typically introduced to prevent erratic or rapid consecutive tab clicks
     * and ensures a smoother user experience by providing a controlled interaction pace.
     */
    private static readonly TAB_CLICK_DELAY = 200;

    /**
     * Sets up a listener for the browser's hash change event and invokes the `open` method whenever the event is triggered.
     *
     * @return {void} Does not return a value.
     */
    static listen(): void {
        this.log('TabbedContent listen');
        this.open();

        window.addEventListener('hashchange', () => {
            this.log('TabbedContent hashchange');
            this.open();
        });
    }

    /**
     * Opens a specific tab and scrolls to a target element based on the configuration parsed from the URL.
     *
     * @return {void} This method does not return a value.
     */
    private static open(): void {
        this.log('TabbedContent open');
        const tabConfig = this.parseTabConfigFromUrl();
        if (!tabConfig) return;

        const targetElement = this.getTargetElement(tabConfig.elementId);
        if (!targetElement) return;

        this.scrollToElement(targetElement);
        this.activateTab(tabConfig.tabIndex);
    }

    /**
     * Parses tab configuration from the current URL's hash segment.
     *
     * @return {TabConfig | null} An object containing the tab's element ID and index, or null if the hash is not present.
     */
    private static parseTabConfigFromUrl(): TabConfig | null {
        const tabUri = new URL(window.location.href);
        const [tabHash, tabNumberString] = tabUri.hash.split('&');

        if (!tabHash) return null;

        const elementId = tabHash.substring(1);
        const tabIndex = this.parseTabIndex(tabNumberString);

        return { elementId, tabIndex };
    }

    /**
     * Parses the provided string to extract and calculate the zero-based tab index value.
     *
     * @param {string} [tabNumberString] - Optional string containing the tab index in the format "tab=X".
     * @return {number} Returns the zero-based tab index value. Defaults to 0 if the input is invalid or not provided.
     */
    private static parseTabIndex(tabNumberString?: string): number {
        if (!tabNumberString) return 0;
        const tabNumber = parseInt(tabNumberString.split('=')[1]);
        return isNaN(tabNumber) ? 0 : tabNumber - 1;
    }

    /**
     * Retrieves the target HTML element with the specified ID prefixed by 'c'.
     *
     * @param {string} elementId - The ID of the element to retrieve, without the 'c' prefix.
     * @return {HTMLElement | null} The HTML element with the ID 'c' + elementId, or null if no matching element is found.
     */
    private static getTargetElement(elementId: string): HTMLElement | null {
        return document.getElementById('c' + elementId);
    }

    /**
     * Scrolls the specified HTML element into view using a pre-defined scroll behavior.
     *
     * @param {HTMLElement} element - The HTML element to scroll into view.
     * @return {void} This method does not return a value.
     */
    private static scrollToElement(element: HTMLElement): void {
        element.scrollIntoView(this.SCROLL_BEHAVIOR);
    }

    /**
     * Activates a tab by its index.
     *
     * @param {number} tabIndex - The index of the tab to activate.
     * @return {void} This method does not return a value.
     */
    private static activateTab(tabIndex: number): void {
        setTimeout(() => {
            const selector = this.TAB_BUTTON_SELECTOR.replace('{index}', tabIndex.toString());
            const tabElement = document.querySelector(selector) as HTMLButtonElement;
            tabElement?.click();
        }, this.TAB_CLICK_DELAY);
    }

    /**
     * Logs a message to the console if the document body element has the 'develop' class.
     *
     * @param {string} message - The message to log to the console.
     * @return {void}
     */
    private static log(message: string): void {
        const body = document.querySelector('body') as HTMLBodyElement;
        if (body?.classList.contains('develop')) {
            console.log(message);
        }
    }
}
